// =============================================================================
// ui/tabs/PlanEditor.tsx — the pre-market plan, editable.
// -----------------------------------------------------------------------------
// The surface D62 said had to wait for a non-lossy round trip. It now has one:
// `planToBlob` merges over the row's stored blob, and `planRoundTrip.test.ts`
// proves it byte-identical against Josh's real 2026-07-30 entry.
//
// ── 🚨 THE FORM NEVER OWNS THE BLOB ──
//
// It edits the TYPED plan. `savePlan` merges that over `rawPlan` — the row's own
// stored object — so a key this client has never modelled survives. If this
// component is ever changed to build the blob itself, three losses come back.
//
// ── WHAT IS DELIBERATELY NOT HERE ──
//
// **THE DAY-LEVEL DISQUALIFIER.** It exists only in the CLASSIC layout
// (`#pm-disq`). Josh runs PHASED, where disqualifiers belong to each HYPOTHESIS —
// index.html:32992 "standalone Disqualifiers section removed", and :33289 keeps the
// legacy value rather than overwriting it. So the value is CARRIED (savePlan still
// writes the stored string to its column, exactly as the phased layout does) and
// the glance band still reads it, but there is no input. A first version of this
// form had one, which is the same error as the levels below.
//
// **KEY LEVELS.** The old client REMOVED those inputs in v2 — its own comment at
// index.html:33262 says the 14 level keys and `lisSummary` "ride along untouched"
// through the plan spread. They are still stored, still carried, and still read by
// `formatPreMarketStructured` for the AI coach prompt, but there is no way to
// enter one. A first version of this form shipped 14 level inputs, which invented
// a feature beta does not have and would have produced data beta cannot show or
// edit. Removed. `planToBlob` now carries those keys through rather than writing
// them, which is exactly what beta's spread does.
//
// The SESSION CONTRACT and the READINESS GATE. Both are on the Pre-Market tab in
// the old client, and both carry state machines a planning form has no business
// driving: the contract locks, records when a daily stop was breached and when
// that was acknowledged (which flags every later trade); the gate stamps
// `readiness_completed_at`. Editing those needs their own surface and their own
// rules. They stay read-only and say so.
//
// Screenshots, recordings and the AI coach are not here either.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutosave } from '../state/useAutosave';
import { useJournalHidden } from '../state/useJournalHidden';
import { useSupabase } from '../state/supabase';
import { JournalSection } from './JournalSection';
import { savePlan } from '../../io/sync/savePlan';
import { planToBlob, stripEmptyNewsRows } from '../../selectors/journal';
import type { PreMarketPlan, HypothesisPattern } from '../../selectors/journal';
import { usePatterns } from '../state/patterns';
import { ScreenshotGrid } from '../media/ScreenshotGrid';

/** Transcribed from the old client. Order matters — it is the menu order. */
const MARKET_REGIME = ['PDC/TRENDING BULLISH', 'BALANCED/RANGING', 'PDC/TRENDING BEARISH'];
const GAMMA_REGIME = ['POSITIVE', 'CALL TRANSITION', 'NEUTRAL', 'PUT TRANSITION', 'NEGATIVE'];
const OVERALL_BIAS = ['BULLISH', 'BULL/NEUTRAL', 'NEUTRAL', 'BEAR/NEUTRAL', 'BEARISH'];

/**
 * The three Market Read cards, in beta's order — and the pairing IS the point.
 *
 * ⚠ EACH REGIME OWNS ITS ANALYSIS BOX. Beta renders one card per row of this
 * table: the label chip, the picker, then the textarea that explains that
 * choice. The port had the three choices in one row and the three explanations
 * in another, so the box about dealer positioning sat two controls away from the
 * gamma regime it describes.
 *
 * ⚠ THE PLACEHOLDERS ARE BETA'S, WORD FOR WORD. They are the only instruction on
 * this screen for what belongs in each box.
 */
const REGIME_FIELDS = [
  {
    key: 'marketRegime', analysisKey: 'marketRegimeAnalysis',
    label: 'Market Regime', options: MARKET_REGIME,
    placeholder: 'What the auction structure / participation / breadth is telling you',
  },
  {
    key: 'gammaRegime', analysisKey: 'gammaRegimeAnalysis',
    label: 'Gamma Regime', options: GAMMA_REGIME,
    placeholder: 'Dealer positioning, gamma flip levels, dealer hedging behavior',
  },
  {
    key: 'overallBias', analysisKey: 'biasSummary',
    label: 'Overall Bias', options: OVERALL_BIAS,
    placeholder: 'Net read — where you lean and what flips it',
  },
] as const satisfies readonly {
  key: 'marketRegime' | 'gammaRegime' | 'overallBias';
  analysisKey: 'marketRegimeAnalysis' | 'gammaRegimeAnalysis' | 'biasSummary';
  label: string;
  options: readonly string[];
  placeholder: string;
}[];

type RegimeField = (typeof REGIME_FIELDS)[number];

const RATINGS = [
  ['mood', 'Mood'], ['focus', 'Focus'], ['energy', 'Energy'], ['stress', 'Stress'],
] as const;

export interface PlanEditorProps {
  plan: PreMarketPlan;
  disqualifiers: string;
  date: string;
  /** The row's stored blob. Never rebuilt here — see the header. */
  rawPlan: Readonly<Record<string, unknown>>;
  baselineUpdatedAt: string | null;
  onSaved: (
    plan: PreMarketPlan, disqualifiers: string,
    updatedAt: string | null, rawPlan: Record<string, unknown>,
  ) => void;
}

export function PlanEditor(
  { plan, disqualifiers, date, rawPlan, baselineUpdatedAt, onSaved }: PlanEditorProps,
) {
  const { sb, userId } = useSupabase();
  const { patterns } = usePatterns();

  const [draft, setDraft] = useState<PreMarketPlan>(plan);
  // ⚠ CARRIED, NOT EDITED. There is no input for this in the phased layout, so the
  // stored value is passed straight back to the writer — which is exactly what the
  // old client's phased branch does (:33292) rather than overwriting it with ''.
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  /**
   * Which hypothesis's pattern menu is open, or null.
   *
   * ⚠ ONE AT A TIME, and closed by a click anywhere else — beta binds a
   * click-outside handler for the same reason. A menu that stays open behind the
   * next one covers the textarea under it.
   */
  /**
   * ⚠ A STRING KEY, not a hypothesis index. Market Read now uses the same chip
   * picker (`h0`, `h1`, … for hypotheses; `marketRegime`, `gammaRegime`,
   * `overallBias` for the regimes), and one piece of state means one
   * click-outside handler and no way for two menus to be open at once.
   */
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // ⚠ Click-OUTSIDE closes it. Bound only while a menu is open, so the app is
  // not carrying a document listener for a control nobody has touched.
  // `mousedown`, not `click`: a click that starts inside and ends outside (a
  // drag over a long pattern name) must not count as leaving.
  useEffect(() => {
    if (openMenu === null) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('.hypo-add-wrap')) return;
      setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenu]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(plan),
    [draft, plan],
  );

  const set = <K extends keyof PreMarketPlan>(k: K, v: PreMarketPlan[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const setRating = (k: 'mood' | 'focus' | 'energy' | 'stress', v: number | null) =>
    setDraft((d) => ({ ...d, selfAssessment: { ...d.selfAssessment, [k]: v } }));

  /** ⚠ Materialises the derived blank row on first edit — see `hypoRows`. */
  const withHypos = (d: PreMarketPlan) =>
    (d.hypotheses.length ? d.hypotheses : [{ setup: '', patterns: [], disqualifier: '' }]);

  const setHypo = (i: number, patch: Partial<PreMarketPlan['hypotheses'][number]>) =>
    setDraft((d) => ({
      ...d,
      hypotheses: withHypos(d).map((h, n) => (n === i ? { ...h, ...patch } : h)),
    }));

  const togglePattern = (i: number, p: HypothesisPattern) =>
    setDraft((d) => ({
      ...d,
      hypotheses: withHypos(d).map((h, n) => {
        if (n !== i) return h;
        const on = h.patterns.some((x) => x.id === p.id);
        return {
          ...h,
          // ⚠ Stores BOTH id and name, the shape the old client writes. Keeping
          // only the id is what made the selection vanish on read.
          patterns: on ? h.patterns.filter((x) => x.id !== p.id) : [...h.patterns, p],
        };
      }),
    }));

  const setNews = (i: number, patch: Partial<PreMarketPlan['newsEvents'][number]>) =>
    setDraft((d) => ({
      ...d,
      newsEvents: d.newsEvents.map((n, x) => (x === i ? { ...n, ...patch } : n)),
    }));

  /**
   * ⚠ REMOVES THE ROW, and re-seeds a blank one if that emptied the list — beta
   * does both in the same handler (index.html:34755-34760).
   *
   * A version of this that cleared the row's three fields instead would look
   * identical on screen for one row and be wrong about every count that follows.
   */
  const removeNews = (i: number) =>
    setDraft((d) => {
      const next = d.newsEvents.filter((_, x) => x !== i);
      return { ...d, newsEvents: next.length ? next : [{ time: '', event: '', plan: '' }] };
    });

  /**
   * ⚠ NEVER BELOW ONE, matching beta's guard on both the button and the splice
   * (index.html:35065, :35167). The button is not rendered for a lone hypothesis,
   * and this refuses anyway — a form with no hypothesis card has nowhere to type.
   *
   * ⚠ Operates on `withHypos`, not `draft.hypotheses`: the first row is derived
   * for render until something is typed into it, and a remove that read the empty
   * draft would index into a different list than the one on screen.
   */
  const removeHypo = (i: number) =>
    setDraft((d) => {
      const rows = withHypos(d);
      return rows.length > 1 ? { ...d, hypotheses: rows.filter((_, n) => n !== i) } : d;
    });

  const revert = useCallback(() => { setDraft(plan); setError(null); }, [plan]);

  /**
   * 🔴 THE BASELINE THIS EDITOR HAS ACTUALLY WRITTEN, which is not always the
   * prop. THE SINGLE MOST IMPORTANT LINE IN THE AUTOSAVE.
   *
   * `savePlan` refuses a write whose `baselineUpdatedAt` is older than the row's
   * stored `updated_at` (`savePlan.ts:98-119`) and answers *"This day was changed
   * somewhere else after you loaded it."* The baseline arrives as a PROP, routed
   * back up through `onSaved` and down again by the parent.
   *
   * ⚠ THAT ROUND TRIP WAS FAST ENOUGH WHEN A HUMAN PRESSED SAVE. It is not fast
   * enough for blur: a second field left before the parent has re-rendered would
   * send the ORIGINAL timestamp, the row would already be newer — because of the
   * FIRST autosave — and the trader would be told their own typing was a conflict
   * from another tab. Carrying the newer value here is what stops that.
   */
  const baselineRef = useRef(baselineUpdatedAt);
  useEffect(() => {
    // ⚠ NEWER ONLY. The prop catching up must not drag the baseline backwards to
    // a value this editor has already superseded.
    const held = baselineRef.current;
    if (!held
      || (baselineUpdatedAt
        && new Date(baselineUpdatedAt).getTime() > new Date(held).getTime())) {
      baselineRef.current = baselineUpdatedAt;
    }
  }, [baselineUpdatedAt]);

  /**
   * ⚠ ONE WRITE AT A TIME. Two overlapping saves race on the same row and the
   * loser reports a conflict the trader did not cause. A blur during a write
   * marks `queued` instead, and the write that is finishing re-runs once.
   */
  const inFlight = useRef(false);
  const queued = useRef(false);

  const save = useCallback(async () => {
    if (inFlight.current) { queued.current = true; return; }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    // ── ⚠ BLANK NEWS ROWS ARE DROPPED HERE, BEFORE THE WRITE ──
    //
    // Not in `planToBlob`: that serialiser's contract is a byte-identical round
    // trip over the row's stored blob, and one that silently dropped list items
    // would make `planRoundTrip.test.ts` assert something it no longer does.
    // Beta strips at the same point — inside its autosave, immediately before
    // `saveStateNow` (index.html:34627).
    //
    // ── 🔴 BETA'S FOCUS GUARD IS PORTED, AND THIS COMMENT USED TO REFUSE IT ──
    //
    // It said the guard was "cargo", on reasoning that was CORRECT at the time:
    // *"here the strip runs only when Save is pressed, so there is no cursor to
    // pull a row out from."* True — until TCR-79 made this run on blur, which
    // inverts it exactly. Add an event, tab from its time field to its event
    // field, and the strip would drop the still-blank row and take the field the
    // trader just tabbed INTO with it. That is the defect beta wrote its guard
    // for (index.html:34600-34603), and it is now reachable here.
    //
    // ⚠ The row is kept, not the dirt: focus leaves the section eventually and
    // the next write strips it then. Nothing permanent is skipped.
    const inNews = typeof document !== 'undefined'
      && !!document.activeElement?.closest('.news-row');
    const rows = inNews ? draft.newsEvents : stripEmptyNewsRows(draft.newsEvents);
    const clean = rows === draft.newsEvents ? draft : { ...draft, newsEvents: [...rows] };
    const r = await savePlan({
      sb, userId, date, plan: clean, disqualifiers, rawPlan,
      baselineUpdatedAt: baselineRef.current,
    });
    inFlight.current = false;
    setSaving(false);
    if (r.error) { setError(r.error); return; }
    // ⚠ The STRIPPED plan becomes the draft, so the screen shows what was
    // actually written. Leaving `draft` alone would redraw the rows that were
    // just dropped and re-arm Save with a diff the user never made.
    setDraft(clean);
    // 🔴 BEFORE anything async can intervene — see `baselineRef` above.
    if (r.updatedAt) baselineRef.current = r.updatedAt;
    // ⚠ Hands back the blob AS WRITTEN, so the next save merges over what is now
    // on disk rather than the copy loaded before this edit.
    onSaved(clean, disqualifiers, r.updatedAt, planToBlob(clean, rawPlan));
    setSavedAt(new Date().toLocaleTimeString());
    if (queued.current) { queued.current = false; void save(); }
  }, [sb, userId, date, draft, disqualifiers, rawPlan, onSaved]);

  /**
   * ⚠ Autosave writes on blur and flushes before the panel is torn down. The
   * Save button below stays — Josh: *"One button at the bottom is fine to give a
   * user something to click because some will want to feel as though they are
   * sure the data saved."* It is reassurance now, not the save path.
   */
  const autosave = useAutosave({ dirty, commit: save });

  /**
   * ⚠ WHICH UNITS THE TRADER HAS HIDDEN (TCR-81). Read here and applied as a
   * CLASS — never by dropping a field from the draft, which the autosave above
   * would then write as a real deletion.
   */
  const { hidden: hiddenUnits } = useJournalHidden();

  const sa = draft.selfAssessment;

  // ⚠ ALWAYS AT LEAST ONE ROW. The old client seeds three blank hypotheses and its
  // save path defaults to one (:33280), so there is always somewhere to type. An
  // empty list with only an "+ Add hypothesis" button is an extra click beta does
  // not ask for. Derived for RENDER only — the draft stays empty until something
  // is typed, so a freshly-opened plan is not immediately "dirty".
  const hypoRows = draft.hypotheses.length
    ? draft.hypotheses
    : [{ setup: '', patterns: [], disqualifier: '' }];

  return (
    <div className="plan-editor">
      <div className="form-grid">
        {/* ── market read ─────────────────────────────────────────────────── */}
        {/* ── 🚨 THREE CARDS, TWO ACROSS — NOT SIX STACKED CONTROLS ──────────
            Josh's side-by-side, 2026-08-03. Beta pairs each regime WITH its
            analysis box inside one card, puts Market Regime and Gamma Regime
            side by side, and runs Overall Bias full width underneath.

            This rendered three `<select>`s in a row and then, separately below
            them, three labelled textareas — so the box explaining the gamma
            read was three controls away from the gamma choice, and every one of
            them sat in a single narrow column.

            ⚠ AND THE PICKER IS A CHIP, NOT A DROPDOWN. Same control as the
            hypothesis pattern picker and the same classes, which is why beta
            builds both from `hypo-add-btn` / `hypo-pat` / `hypo-menu`: choose
            one, it becomes a chip with an × to clear it. The `<select>` showing
            "— none —" is a different control that happens to hold the same
            value.

            ⚠ SINGLE-SELECT, so the menu holds RADIOS and carries its own
            "— None —" row. The hypothesis picker below is multi-select and uses
            checkboxes; sharing the markup does not mean sharing the semantics. */}
        <Sec icon="◈" title="Market Read" hideId="pre:marketRead">
          {/* 🔴 THE SURVIVOR OF THE TOP PAIR SPANS BOTH COLUMNS (TCR-83). Josh:
              *"if I hide the Gamma Regime field, Market Regime should widen to
              fill the gap."* Hiding is `display:none`, so a hidden card leaves
              its grid column empty rather than collapsing it.

              ⚠ ONLY THE FIRST TWO. Overall Bias is the third card and already
              spans, via `.pm-regime-bias > .hypo-card:nth-child(3)` — which is
              POSITIONAL, and keeps working because hidden cards stay mounted.

              ⚠ Decided here rather than in CSS because there is no selector for
              "my sibling is hidden", and the hidden set is already in hand. */}
          <div className="pm-regime-bias">
            {REGIME_FIELDS.map((f, i) => (
              <RegimeCard
                key={f.key}
                field={f}
                value={draft[f.key]}
                analysis={draft[f.analysisKey]}
                open={openMenu === f.key}
                onToggleMenu={() => setOpenMenu((cur) => (cur === f.key ? null : f.key))}
                onPick={(v) => { set(f.key, v); setOpenMenu(null); }}
                onAnalysis={(v) => set(f.analysisKey, v)}
                /* ⚠ BETA'S THREE HIDEABLE FIELD KEYS ARE EXACTLY THESE (TCR-81). */
                hiddenHere={hiddenUnits.includes(f.key)}
                /* ⚠ Exactly one of the top PAIR visible → that one spans. */
                spanBoth={i < 2 && hiddenUnits.includes(
                  REGIME_FIELDS[i === 0 ? 1 : 0]!.key,
                ) && !hiddenUnits.includes(f.key)}
                onBlur={autosave.onBlur}
              />
            ))}
          </div>
        </Sec>

        {/* ── chart screenshots ───────────────────────────────────────────── */}
        {/* ⚠ POSITION IS BETA'S: between Market Read and News & Events
            (index.html:32974), not appended at the end. It is the first thing a
            trader adds after writing the regime, and beta puts it there.

            ⚠ AND IT LIVES IN THE DRAFT, which is the whole reason it is inside
            this form rather than a section of its own on the tab. `planToBlob`
            writes `draft.screenshots`, so a sibling component persisting a
            screenshot independently would be erased by the next plan Save —
            D61's stale-blank-as-authorised-delete, one layer up. One writer, one
            save unit. */}
        <Sec icon="⛶" title="Chart Screenshots" hideId="sec:screenshots"
          meta="click, paste or drop — click a thumbnail to view it full size">
          <ScreenshotGrid
            files={draft.screenshots}
            prefix={`${date}-premarket`}
            onChange={(next) => set('screenshots', next)}
          />
          {/* ⚠ SAYS WHAT THIS CLIENT DOES, not what beta does. Beta's own hint
              reads "persists immediately" because it autosaves the whole entry;
              here the image is uploaded at once but the plan still has to be
              saved. Copying beta's sentence would be a lie the user only
              discovers by losing a reference. */}
          <p className="muted shot-hint">
            The image uploads straight away — <b>Save plan</b> is what attaches it to this
            day. Removing a thumbnail un-attaches it; the file itself is kept.
          </p>
        </Sec>

        {/* ── news ────────────────────────────────────────────────────────── */}
        {/* ⚠ BEFORE Setups hunting — beta's order (:32983 news, :32990 setups).
            This form had them the other way round, which is a composition
            defect under D85: membership was right and order was not. */}
        <Sec icon="▣" title="News &amp; Events" hideId="sec:news">
          {/* ⚠ BETA'S SENTENCE, AND THE SECOND HALF OF IT IS LOAD-BEARING. A form
              that deletes rows on save without saying so is indistinguishable
              from one that loses them. Beta prints this above the rows
              (index.html:34262) and it is the only warning either client gives. */}
          <p className="muted news-hint">
            For each event: when it’s scheduled, what it is, and how you’ll play it.
            Empty rows are dropped on save.
          </p>
          {draft.newsEvents.map((n, i) => (
            <div className="row gap-8 news-row" key={i}>
              <input aria-label={`News ${i + 1} time`} placeholder="8:30"
                value={n.time} onChange={(e) => setNews(i, { time: e.target.value })} onBlur={autosave.onBlur} />
              <input aria-label={`News ${i + 1} event`} placeholder="Event"
                value={n.event} onChange={(e) => setNews(i, { event: e.target.value })} onBlur={autosave.onBlur} />
              <input aria-label={`News ${i + 1} plan`} placeholder="How you’ll trade it"
                value={n.plan} onChange={(e) => setNews(i, { plan: e.target.value })} onBlur={autosave.onBlur} />
              {/* ⚠ ON EVERY ROW, including the only one — beta puts no condition
                  on this button (index.html:34707) because removing the last row
                  re-seeds a blank one rather than emptying the section. */}
              <button
                type="button" className="btn btn-icon news-remove"
                aria-label={`Remove news ${i + 1}`} title="Remove event"
                onClick={() => removeNews(i)}
              >×</button>
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => setDraft((d) => ({
            ...d, newsEvents: [...d.newsEvents, { time: '', event: '', plan: '' }],
          }))}>+ Add event</button>
        </Sec>

        {/* ── hypotheses ──────────────────────────────────────────────────── */}
        {/* ⚠ NO `meta` HERE ANY MORE. It read "what you'll wait for, and what
            invalidates it" — written for this client, not transcribed. Beta's
            header carries nothing (index.html:34266) and puts its guidance in a
            muted line inside the body, below. Two summaries of the same section
            is one more than beta shows. */}
        <Sec icon="⌖" title="Setups Hunting" hideId="pre:setupsHunting">
          {/* ⚠ WHERE THE "THREE" ACTUALLY LIVES. Beta prints this and enforces
              nothing (index.html:34268). Word for word — it is also the only
              place either client says the pattern list tracks the Patterns
              tab. */}
          <p className="muted hypo-hint">
            Up to three hypotheses for today’s session. Pick one or more patterns per
            hypothesis and describe the conditions you’ll wait for. Pattern list syncs
            live with the Patterns tab.
          </p>
          {/* ⚠ `hypo-card-2col` IS the old client's side-by-side conditions /
              disqualifier layout — a class named for exactly the thing this port
              had stacked. `npm run relocate -- hypo-` reported 11 of 13 missing,
              including this one. The two textareas share a grid ROW so they
              top-align however tall the pattern row grows. */}
          {hypoRows.map((h, i) => (
            <div className="hypo-card hypo-card-2col" key={i}>
              <div className="hypo-toprow">
                <span className="hypo-num">Hypothesis {i + 1}</span>
                {/* ⚠ ONLY WHEN THERE IS MORE THAN ONE — beta's own condition
                    (index.html:35065 `phased && hypos.length > 1`). */}
                {hypoRows.length > 1 ? (
                  <button
                    type="button" className="hypo-remove"
                    aria-label={`Remove hypothesis ${i + 1}`}
                    title="Remove this hypothesis"
                    onClick={() => removeHypo(i)}
                  >×</button>
                ) : null}
              </div>
              {/* ⚠ SELECTED PATTERNS ARE CHIPS; the rest live behind a menu.
                  This rendered every pattern in the catalogue as a permanent
                  checkbox — ten of them on Josh's book, always on screen, where
                  beta shows the two he picked and a `+ select patterns` button.
                  `hypo-pat` / `hypo-add-wrap` / `hypo-add-btn` / `hypo-menu`
                  were all already written in the old client. */}
              <div className="hypo-pats">
                {h.patterns.map((sel) => {
                  // ⚠ An ORPHAN is a pattern tagged here and since deleted from
                  // the catalogue. Beta keeps it visible and marked rather than
                  // dropping it — silently losing a tag the user set is worse
                  // than showing one that no longer resolves.
                  const live = (patterns ?? []).some((x) => String(x.id) === sel.id);
                  return (
                    <span key={sel.id} className={`hypo-pat${live ? '' : ' orphan'}`}>
                      {sel.name || '?'}
                      {live ? null : <span className="muted"> (deleted)</span>}
                      <span
                        className="x" role="button" tabIndex={0}
                        aria-label={`Remove ${sel.name || 'pattern'}`}
                        onClick={() => togglePattern(i, { id: sel.id, name: sel.name })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            togglePattern(i, { id: sel.id, name: sel.name });
                          }
                        }}
                      >×</span>
                    </span>
                  );
                })}
                <div className="hypo-add-wrap">
                  <button
                    type="button" className="hypo-add-btn"
                    aria-expanded={openMenu === `h${i}`}
                    onClick={() => setOpenMenu((cur) => (cur === `h${i}` ? null : `h${i}`))}
                  >
                    {h.patterns.length ? '+ pattern' : '+ select patterns'}
                  </button>
                  <div className={`hypo-menu${openMenu === `h${i}` ? ' open' : ''}`}>
                    {(patterns ?? []).length ? (patterns ?? []).map((p) => {
                      const on = h.patterns.some((x) => x.id === String(p.id));
                      return (
                        <label key={String(p.id)}>
                          <input
                            type="checkbox" checked={on}
                            onChange={() => togglePattern(i, { id: String(p.id), name: p.name })}
                          />
                          {p.name || 'Untitled'}
                        </label>
                      );
                    }) : (
                      <div className="muted hypo-menu-empty">
                        No patterns in the catalogue yet — add some on the Patterns tab.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* ⚠ The label sits in its own grid cell above the right-hand
                  textarea, so `<Area>`'s label+box wrapper cannot be used here —
                  the grid places five children, not two. */}
              <label className="hypo-disq-label" htmlFor={`pl-h${i}-dq`}>Disqualifier</label>
              <textarea
                id={`pl-h${i}-setup`} className="hypo-setup-ta" rows={2}
                aria-label={`Hypothesis ${i + 1}`}
                placeholder="What conditions trigger this hypothesis — entry, confirmation, invalidation"
                value={h.setup}
                onChange={(e) => setHypo(i, { setup: e.target.value })}
                onBlur={autosave.onBlur}
              />
              <textarea
                id={`pl-h${i}-dq`} className="hypo-disq-ta" rows={2}
                aria-label={`Disqualifier ${i + 1}`}
                placeholder="If this happens, skip the trade"
                value={h.disqualifier}
                onChange={(e) => setHypo(i, { disqualifier: e.target.value })}
                onBlur={autosave.onBlur}
              />
            </div>
          ))}
          {/* ── 🔴 THERE IS NO CAP, AND THE COMMENT THAT USED TO SIT HERE WAS
                 WRONG ABOUT BETA ──

              It read "The old client caps this at three" and hid the button at
              three on the strength of it. Beta's handler
              (index.html:35155-35161) pushes unconditionally, and the button
              itself is emitted with no length test (:35123). The "three" comes
              from the GUIDANCE SENTENCE above the cards (:34268) — advice, never
              enforced. This port kept the enforcement and dropped the sentence,
              which is the wrong half of both.

              ⚠ The cap was also unreachable in the other direction: nothing
              could remove a hypothesis, so a plan that arrived with four — which
              beta can write — was stuck at four with no way down and no way to
              add. */}
          <button type="button" className="btn btn-sm" onClick={() => setDraft((d) => ({
            ...d,
            hypotheses: [...withHypos(d), { setup: '', patterns: [], disqualifier: '' }],
          }))}>+ Add hypothesis</button>
        </Sec>

        {/* ── self-assessment ─────────────────────────────────────────────── */}
        <Sec icon="❤" title="Self-Assessment" hideId="sec:selfassessment"
          meta="1 = poor, 3 = neutral, 5 = great · Stress: 1 = none, 5 = overwhelmed · clear to leave unset">
          <div className="recap-grid form-grid">
            <Num id="pl-sleep" label="Sleep (hours)" step="0.5" value={sa.sleepHours}
              onChange={(v) => setDraft((d) => ({
                ...d, selfAssessment: { ...d.selfAssessment, sleepHours: v },
              }))} />
            <Num id="pl-caff" label="Caffeine (cups)" step="1" value={sa.caffeineCups}
              onChange={(v) => setDraft((d) => ({
                ...d, selfAssessment: { ...d.selfAssessment, caffeineCups: v },
              }))} />
          </div>
          {/* ⚠ RELOCATED to the old client's `sa-*` classes. These stacked in ONE
              column because this client had NONE of the four rules that lay them
              out two across — `npm run relocate -- sa-` reported 4 of 4 missing.
              The markup here was written to a stylesheet that did not exist. */}
          <div className="sa-grid">
            {RATINGS.map(([k, label]) => {
              const set = typeof sa[k] === 'number';
              return (
                <div className={`sa-rating-row${set ? '' : ' unset'}`} key={k}>
                  <label htmlFor={`pl-sa-${k}`}>{label}</label>
                  <input
                    id={`pl-sa-${k}`}
                    type="range" min="1" max="5" step="1"
                    aria-label={label}
                    value={set ? (sa[k] as number) : 3}
                    onChange={(e) => setRating(k, Number(e.target.value))}
                  />
                  <span className="sa-display">{set ? `${sa[k]}/5` : '—'}</span>
                  {/* ⚠ CLEAR, not "set to 3". The old client's × deletes the key,
                      and unset is a real state that renders "—". A slider alone
                      cannot express it, which is why this button exists. */}
                  <button type="button" className="sa-clear" aria-label={`Clear ${label}`}
                    onClick={() => setRating(k, null)}>×</button>
                </div>
              );
            })}
          </div>
        </Sec>
      </div>

      {error ? (
        <div role="alert" className="import-alert"><b>Nothing was saved.</b> {error}</div>
      ) : null}

      <div className="import-row">
        <button type="button" className="btn primary" disabled={saving || !dirty}
          onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save plan'}
        </button>
        <button type="button" className="btn" disabled={saving || !dirty} onClick={revert}>
          Revert
        </button>
        <span className="muted">
          {dirty ? 'Unsaved changes' : savedAt ? `Saved at ${savedAt}` : 'No changes'}
        </span>
      </div>

      {/* ⚠ THIS SENTENCE HAS GONE STALE ONCE ALREADY TODAY. It said the session
          contract was "not editable here" while the contract editor rendered
          directly below it. Prose describing a cut line goes stale every time the
          cut line moves, and no test catches it — so keep it about THIS FORM and
          let the other surfaces speak for themselves. */}
      <p className="muted">
        This form saves the pre-market plan only. The session contract below has its own
        Lock button and its own save; the readiness gate, screenshots, voice notes and
        Coach Me are still current-client only.
      </p>
    </div>
  );
}

// ── small controls ──────────────────────────────────────────────────────────
//
// ⚠ `Select` IS DELETED, not left for later. Its only callers were the three
// Market Read dropdowns, which are now chip pickers — beta has no `<select>` on
// this screen at all. A component with no caller reads as a control the screen
// offers, and the next person to need a dropdown here would reach for it and
// re-introduce exactly what Josh's side-by-side rejected.

/**
 * One Market Read card: label chip, single-select chip picker, analysis box.
 *
 * ── ⚠ WHY IT IS NOT A `<select>` ──
 *
 * Beta builds this from the SAME controls as the hypothesis pattern picker —
 * `hypo-add-btn` to open, `hypo-pat` for the chosen value with an × to clear,
 * `hypo-menu` for the list. A dropdown reading "— none —" holds the same value
 * and is a different control: it never shows the choice as a removable chip, and
 * on this screen the two pickers then look unrelated when they are the same
 * gesture.
 *
 * ⚠ RADIOS, not checkboxes. This picks ONE. The hypothesis picker below shares
 * every class and picks several — the markup is shared, the semantics are not,
 * and the input type is the only thing carrying that difference.
 *
 * ⚠ A STORED VALUE THE LIST NO LONGER CONTAINS IS STILL OFFERED, and still
 * shows as the chip. Beta's dropdown had the same guard for the same reason: a
 * regime retired from the list is still on last month's plans, and a picker that
 * cannot represent it turns the next save into a silent rewrite.
 */
function RegimeCard(
  { field, value, analysis, open, onToggleMenu, onPick, onAnalysis, onBlur, hiddenHere, spanBoth }:
  {
    field: RegimeField;
    value: string;
    analysis: string;
    open: boolean;
    onToggleMenu: () => void;
    onPick: (v: string) => void;
    onAnalysis: (v: string) => void;
    onBlur: () => void;
    hiddenHere: boolean;
    spanBoth: boolean;
  },
) {
  const options = value && !field.options.includes(value)
    ? [...field.options, value]
    : field.options;

  return (
    <div
      className={`hypo-card${hiddenHere ? ' jp-hidden' : ''}${spanBoth ? ' span-2' : ''}`}
      data-hide-id={field.key}
    >
      <span className="hypo-num">{field.label}</span>
      <div className="hypo-pats">
        <div className="hypo-add-wrap">
          {/* ⚠ 🚨 THE CHIP IS THE TRIGGER — THERE IS NO SECOND BUTTON. A first
              pass rendered the chip AND a "+ change" button beside it. Beta
              swaps one for the other: no value → the `+ select …` button; a
              value → the chip alone, `title="Click to change"`. Adding the
              button was inventing a control beta does not have, which is the
              exact failure mode the screenshot rule exists to stop, committed
              while porting from a screenshot.

              ⚠ `role="button"` + `tabIndex` because beta uses a `<span>` and a
              real `<button>` cannot legally contain the × (which is itself a
              control). The keyboard reaches both. */}
          {value ? (
            <span
              className="hypo-pat" role="button" tabIndex={0}
              aria-expanded={open}
              aria-label={`${field.label}: ${value}. Click to change.`}
              onClick={onToggleMenu}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleMenu(); }
              }}
            >
              {value}
              <span
                className="x" role="button" tabIndex={0}
                aria-label={`Clear ${field.label.toLowerCase()}`}
                onClick={(e) => { e.stopPropagation(); onPick(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); e.stopPropagation(); onPick('');
                  }
                }}
              >×</span>
            </span>
          ) : (
            <button
              type="button" className="hypo-add-btn"
              aria-expanded={open}
              onClick={onToggleMenu}
            >
              + select {field.label.toLowerCase()}
            </button>
          )}
          <div className={`hypo-menu${open ? ' open' : ''}`}>
            {/* ⚠ "— None —" IS AN OPTION, not the absence of one. Clearing has to
                be reachable from inside the menu as well as from the chip's ×,
                because a user who opened the menu to change their mind should
                not have to close it again to unset. Beta lists it first. */}
            <label>
              <input
                type="radio" name={`pm-${field.key}`} checked={value === ''}
                onChange={() => onPick('')}
              />{' '}
              <span className="muted">— None —</span>
            </label>
            {options.map((o) => (
              <label key={o}>
                <input
                  type="radio" name={`pm-${field.key}`} checked={o === value}
                  onChange={() => onPick(o)}
                />{' '}
                {o}
              </label>
            ))}
          </div>
        </div>
      </div>
      {/* ⚠ `aria-label`, not a visible `<label>`: the chip above IS the heading,
          and beta emits a bare textarea here. A second visible label would print
          "Market Regime" twice in one card. */}
      <textarea
        rows={2}
        aria-label={`${field.label} analysis`}
        placeholder={field.placeholder}
        value={analysis}
        onChange={(e) => onAnalysis(e.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

// ⚠ `Area` went with `Select`, and for the same reason. Its three callers were
// the analysis boxes, which now live inside their own regime's card with the
// chip as their heading — the pairing is the whole point of the change. The
// comment above the hypothesis textareas still names it because it explains why
// those were never wrapped in one either.

function Num(
  { id, label, step, value, onChange }:
  { id: string; label: string; step: string; value: number | null; onChange: (v: number | null) => void },
) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      <input
        id={id} type="number" step={step} inputMode="decimal" value={value ?? ''}
        onChange={(e) => onChange(e.target.value.trim() === '' ? null : (Number(e.target.value) || null))}
      />
    </div>
  );
}

/**
 * One collapsible plan section — the old client's `.jp-sec` card.
 *
 * ⚠ THE CARD ITSELF NOW LIVES IN `JournalSection.tsx`, because there were two
 * copies of it and only this one was right: `Journal.tsx` rendered the same
 * class name as a bare heading with no header row, so the Session and Recap
 * phases never got the chrome this one did.
 *
 * ⚠ THE ICONS ARE BETA'S, per section (index.html:32971+): ◈ market read,
 * ⛶ screenshots, ▣ news, ⌖ setups, ❤ self-assessment. Same glyph per concept so
 * the two clients read alike.
 */
const Sec = JournalSection;
